/**
 * END TURN's confirm (`projects/turn-presence.md` Phase 3). Every END TURN site (the command menu's
 * foot, the inspector header, the granted-turn strip) runs through this one hook so they cannot
 * drift.
 *
 * With no actions left, one click ends the turn. With actions left, the first click arms the same
 * button (label {@link END_TURN_CONFIRM_LABEL}, `armed` for the `is-confirm` style) and the second
 * ends the turn. It disarms after {@link END_TURN_CONFIRM_MS}, on pointer leave, on Esc (a layer-1
 * entry, `decisions/0189`), and whenever the unit's action points change. Enter and Space press the
 * native button, so the keyboard confirms the same way.
 */
import { useContext, useEffect, useId, useState } from "react";
import { hasStatus, type Combatant } from "@lancer-vtt/domain";
import { EscLayersContext, useEscEntry } from "./escLayers";

export const END_TURN_LABEL = "END TURN";
export const END_TURN_CONFIRM_LABEL = "ARE YOU SURE? YOU HAVE ACTIONS LEFT";
export const END_TURN_CONFIRM_MS = 4000;

/**
 * Whether ending the turn now would forfeit an action. RAW a turn is a standard move plus two quick
 * actions or one full action (core book, the turn structure), so an action point left counts and
 * unspent movement does not. A Stunned unit has no actions to lose. A Shut Down unit counts its AP:
 * Boot Up is a full action it can still take.
 */
export function actionsLeftToEnd(args: { ap: number; combatant: Pick<Combatant, "statuses"> }): boolean {
  return args.ap > 0 && !hasStatus(args.combatant.statuses, "Stunned");
}

export type ConfirmEndTurn = {
  label: string;
  armed: boolean;
  onClick: () => void;
  onPointerLeave: () => void;
};

export function useConfirmEndTurn(args: {
  /** {@link actionsLeftToEnd}: arm on the first click only when this holds. */
  actionsLeft: boolean;
  /** The unit's action points: a change disarms. */
  ap: number;
  onEnd: () => void;
}): ConfirmEndTurn {
  const { actionsLeft, ap, onEnd } = args;
  // The AP the button was armed at: armed only while the unit still has exactly that many.
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const armed = actionsLeft && armedAt === ap;
  const disarm = () => setArmedAt(null);
  const id = useId();

  // Forget the arm once AP moves, so an undo that restores the old AP does not re-arm the button.
  useEffect(() => setArmedAt(null), [ap]);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmedAt(null), END_TURN_CONFIRM_MS);
    return () => clearTimeout(t);
  }, [armed]);

  useEscEntry(useContext(EscLayersContext), { layer: "popover", id: `end-turn-confirm-${id}`, active: armed, onDismiss: disarm });

  return {
    label: armed ? END_TURN_CONFIRM_LABEL : END_TURN_LABEL,
    armed,
    onClick: () => {
      if (actionsLeft && !armed) {
        setArmedAt(ap);
        return;
      }
      disarm();
      onEnd();
    },
    onPointerLeave: disarm,
  };
}
